/*
 * zona-v2.js — shared interactivity modules for the ZONA v2 sandbox theme.
 *
 * Loaded once from layout/theme.liquid with `defer`. Every module is
 * attribute-driven and self-initialising: sections emit markup carrying the
 * documented attributes, this file wires the behaviour. No section-specific
 * logic lives here.
 *
 * GLOBAL REDUCED-MOTION HOOK
 * --------------------------
 * At init, if matchMedia("(prefers-reduced-motion: reduce)").matches, the class
 * `zona-reduced` is added to <html> (document.documentElement). Section CSS in
 * later tasks (V6/V8/V9) MUST use this hook to suppress the per-child
 * `transition-delay` values Liquid bakes into the staggered reveal children —
 * otherwise a reduced-motion user still sees the full stagger, just starting
 * immediately instead of on scroll (INV row 14 forbids this). Write, scoped to
 * whatever selector that section actually uses for its staggered children:
 *
 *     .zona-reduced [data-vsrest-row] { transition-delay: 0s !important; }
 *     .zona-reduced [data-day-dot] { transition-delay: 0s !important; }
 *
 * Do NOT re-implement `@media (prefers-reduced-motion: reduce)` per section —
 * one forgotten section reintroduces the bug. Use the shared class.
 *
 * MARKUP CONTRACT (frozen — later section tasks emit exactly these names)
 * ---------------------------------------------------------------------
 *  1. Accordion
 *       [data-zona-accordion]              root (may contain many pairs; may nest)
 *         [data-acc-trigger]               button, owns aria-expanded
 *         [data-acc-panel]                 collapsible panel (paired by index)
 *       Collapse variants (attribute may sit on the panel or on the root):
 *         (default)                        measured max-height (panel.scrollHeight)
 *         data-acc-maxheight="1200"        fixed max-height:{n}px on open
 *         data-acc-gridrows                grid-template-rows 1fr/0fr on the panel
 *       Optional data-acc-scroll (trigger, panel or root): on OPEN only,
 *       rAF -> panel.scrollIntoView, media query read LIVE at click time.
 *       aria-expanded="true" in markup starts that pair open.
 *       data-acc-exclusive                 OPT-IN single-open group. Put it on the
 *         group root — either the [data-zona-accordion] element itself, or any
 *         ancestor wrapping several [data-zona-accordion] instances. Opening any
 *         pair inside that root closes every other pair inside the SAME root, via
 *         each pair's own paint() — so it works across all three collapse
 *         variants, which may coexist in one group. Source's BOTH accordion
 *         usages are single-open: FAQ (useState<number|null>) and PdpAccordions
 *         (useState<string|null>, rendered twice — each instance is its own
 *         group, so each instance's wrapper carries its own data-acc-exclusive).
 *         Nesting-safe: a pair nested INSIDE the opening pair's panel, and a pair
 *         whose panel CONTAINS the opening trigger, are never auto-closed.
 *
 *  2. Reveal — TWO distinct forms, never conflated:
 *       Form A (self-reveal):  [data-reveal] [data-reveal-threshold="0.15"]
 *         observed element === revealed element; gets .is-visible itself.
 *       Form B (trigger -> scope): [data-reveal-trigger] [data-reveal-trigger-threshold="0.15"]
 *         observed element is the trigger; .is-visible lands on the element
 *         carrying [data-reveal-scope], which MAY be the trigger itself.
 *         Scope resolution order:
 *           a) non-empty value on data-reveal-trigger, used as a CSS selector
 *              ("#id" / ".cls" / "[attr]") or as a bare id / scope-value token,
 *           b) the trigger itself, if it also carries data-reveal-scope,
 *           c) first [data-reveal-scope] inside the trigger's subtree,
 *           d) nearest ancestor containing a [data-reveal-scope], first in
 *              document order,
 *           e) the trigger itself (last resort).
 *       Optional data-reveal-rootmargin="<css margin string>" on the
 *         OBSERVED element (Form A: the element itself; Form B: the trigger)
 *         sets that pair's IntersectionObserver rootMargin. Absent = "0px"
 *         (today's behaviour — every existing homepage consumer is
 *         unaffected).
 *       Both forms group their elements by distinct (threshold, rootMargin)
 *       pair into separate IntersectionObservers, independently of each
 *       other, and call observer.unobserve(entry.target) — never
 *       disconnect() — on first intersection, so co-grouped elements keep
 *       their observer.
 *       [data-reveal-reduced-check] on the observed element (either form):
 *         under prefers-reduced-motion, .is-visible is added to the TARGET
 *         immediately at init and the element is never observed.
 *
 *  3. StaggerDelay — no JS. Per-child transition-delay values are unrolled
 *       into inline styles at build time by Liquid. vs-rest's 8 data rows use
 *       120ms 165ms 210ms 255ms 300ms 345ms 390ms 435ms (120 + i*45, i=0..7).
 *       Form B's single .is-visible on the scope unlocks them all at once.
 *
 *  4. Sticky bar
 *       [data-zona-sticky]                 the bar (optional value = sentinel selector)
 *       [data-sticky-sentinel]             boundary element, anywhere in the DOM
 *       Passive scroll listener; shown when sentinel.getBoundingClientRect().bottom <= 0.
 *
 *  5. Idle-deferred video — two independent gates:
 *       [data-zona-idlevideo]              TIME gate (hero): requestIdleCallback(mount,
 *                                          {timeout:3000}) else setTimeout(mount,1500)
 *       [data-zona-idlevideo-viewport]     VIEWPORT gate (egg background):
 *                                          IntersectionObserver rootMargin "200px 0px",
 *                                          mounts once then unobserves
 *       Both read data-src, set video.src + video.load(); optional
 *       data-playback-rate applied on loadedmetadata; autoplay play() is
 *       promise-guarded with .catch().
 *
 *  6. Two-phase scroll correction
 *       [data-zona-scrollto] with data-scrollto-target="<selector>"
 *       smooth scrollIntoView, then whichever fires first of window "scrollend"
 *       ({once:true}) or a 1600ms timeout re-reads the rect and does a
 *       corrective scrollIntoView({behavior:"instant"}).
 *
 *  7. Modal / bottom sheet
 *       [data-zona-modal]                  sheet root (fixed overlay)
 *         [data-modal-backdrop]            optional; else the root is the backdrop
 *         [data-modal-sheet]               optional; else the root is transformed
 *         [data-modal-close]               optional close button(s)
 *       Backdrop timing is SEPARATE from the sheet's: backdrop defaults to
 *       "opacity 0.28s ease" (source), sheet to "transform 0.32s
 *       cubic-bezier(0.32, 0.72, 0, 1)". Override the backdrop's with
 *       data-modal-backdrop-transition="<css transition>" on the backdrop or
 *       the root.
 *       Because both child markers are optional, the root can play several
 *       roles at once and its `transition` is assembled as ONE shorthand list
 *       covering every role it actually holds:
 *         backdrop absent  -> the root's own opacity IS the backdrop fade, so
 *                             the root carries the BACKDROP timing (0.28s ease
 *                             / the override), never the sheet's.
 *         backdrop present -> the root's opacity moves with the sheet timing
 *                             and the marked backdrop owns the 0.28s fade.
 *         sheet absent     -> "transform <sheet timing>" is appended to the
 *                             root's list instead of assigned separately.
 *         always           -> "visibility <sheet timing>", so the root stays
 *                             visible until the slowest role has finished.
 *       [data-zona-modal-trigger] + [data-modal-target="<selector>"] opens it
 *       from ANYWHERE in the DOM (document-delegated click).
 *       Body scroll lock, Escape (listener only while open), backdrop click,
 *       close button. NO focus trap — faithful to source.
 *
 *  8. Disclosure — ONE-WAY, never re-collapses. Not an Accordion variant.
 *       [data-zona-disclosure] > [data-disc-trigger] + [data-disc-panel]
 *
 *  9. Nav disclosure — plain two-way toggle, panel genuinely absent when closed.
 *       [data-zona-navtoggle] (optional value / aria-controls = panel selector)
 *       [data-nav-panel]
 *
 * 10. Gallery — independent state per [data-zona-gallery] instance.
 *       [data-gallery-slide] (ordered), [data-gallery-thumb], one
 *       [data-gallery-badge] that is a SIBLING of the slides, visible only
 *       while active === 0. Touch swipe threshold |dx| > 40.
 *
 * 11. Shared pricing state — module-scope "subscribe" (default) | "onetime".
 *       [data-purchase-card="subscribe"|"onetime"] click/Enter/Space anywhere
 *       sets the plan and calls applyAll(). applyAll() also runs ONCE on init.
 *       applyAll()'s authoritative consumer list:
 *         (a) every [data-purchase-card]  -> aria-checked + .is-selected
 *         (b) every input[name="selling_plan"] -> value + disabled
 *         (c) [data-sperm-price]          -> "$199" + "/mo" vs "$249" + "/box"
 *       Do not add a 4th consumer without updating the plan contract.
 *
 * 12. Reading progress
 *       [data-zona-reading-progress]       fixed track (3px)
 *         [data-reading-progress-fill]     fill element; width set live
 *       fill.style.width = pct + "%" where
 *         pct = min(100, scrollTop / (scrollHeight - clientHeight) * 100),
 *         0 when the page does not scroll. Passive scroll listener, rAF-
 *         throttled (Deviation 5: source 81-90 sets React state on every
 *         scroll event; here the paint is gated to one per animation
 *         frame instead — visual result identical, fewer style writes).
 *         gated; runs once at init.
 */
(function () {
  "use strict";

  var ZonaV2 = (window.ZonaV2 = window.ZonaV2 || {});

  /* ------------------------------------------------------------------ utils */

  function $all(selector, root) {
    return Array.prototype.slice.call((root || document).querySelectorAll(selector));
  }

  /* Children owned by this root — skips descendants belonging to a nested
     instance of the same module (e.g. an accordion inside an accordion). */
  function scopedAll(root, childSelector, rootSelector) {
    return $all(childSelector, root).filter(function (el) {
      return el.closest(rootSelector) === root;
    });
  }

  function reducedMotion() {
    return !!(window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches);
  }

  /* Read the media query LIVE at call time — never cached at init. */
  function scrollBehavior() {
    return reducedMotion() ? "auto" : "smooth";
  }

  function safeQuery(selector) {
    if (!selector) return null;
    try {
      return document.querySelector(selector);
    } catch (e) {
      return null;
    }
  }

  function numberAttr(el, name, fallback) {
    var raw = el.getAttribute(name);
    if (raw === null || raw === "") return fallback;
    var n = parseFloat(raw);
    return isNaN(n) ? fallback : n;
  }

  /* Diagnostic only — never changes behaviour. Fires ONLY when a same-page
     fallback picks a target that was not explicitly pointed at, which can grab
     a different section's element entirely if a later section (V4-V10) forgets
     an attribute. */
  function warnFallback(message, el) {
    if (window.console && console.warn) console.warn("[zona-v2] " + message, el);
  }

  function describe(el) {
    if (!el) return "(none)";
    var out = el.tagName ? el.tagName.toLowerCase() : "?";
    if (el.id) out += "#" + el.id;
    if (el.className && typeof el.className === "string" && el.className.trim()) {
      out += "." + el.className.trim().split(/\s+/).join(".");
    }
    return out;
  }

  function guard(name, fn) {
    try {
      fn();
    } catch (e) {
      if (window.console && console.warn) console.warn("[zona-v2] " + name + " failed:", e);
    }
  }

  /* -------------------------------------------------------------- accordion */

  var ACC_TRANSITION_EASE = "cubic-bezier(0.4, 0, 0.2, 1)";
  var ACC_TRANSITION_TIME = "0.38s";

  function initAccordions() {
    /* Every trigger/panel pair on the page, with the exclusivity group root it
       belongs to (null when it is not in one). Populated as roots are walked;
       the click handlers close over this array, so pairs registered later in
       the pass are still visible to pairs registered earlier. */
    var accPairs = [];

    /* Close every OTHER pair sharing this pair's exclusivity root, reusing that
       pair's own close path — so the correct collapse variant is used for each
       (measured max-height / data-acc-maxheight / data-acc-gridrows may coexist
       inside one group). */
    function closeSiblings(opened) {
      if (!opened.exclusiveRoot) return;
      accPairs.forEach(function (other) {
        if (other === opened) return;
        if (other.exclusiveRoot !== opened.exclusiveRoot) return;
        /* Nesting guards: never close a pair nested inside the panel we just
           opened, and never close an ancestor pair whose panel contains the
           trigger that was just clicked. */
        if (opened.panel.contains(other.trigger)) return;
        if (other.panel.contains(opened.trigger)) return;
        other.close();
      });
    }

    $all("[data-zona-accordion]").forEach(function (root) {
      var triggers = scopedAll(root, "[data-acc-trigger]", "[data-zona-accordion]");
      var panels = scopedAll(root, "[data-acc-panel]", "[data-zona-accordion]");

      triggers.forEach(function (trigger, i) {
        var panel = panels[i];
        if (!panel) return;

        var gridRows = panel.hasAttribute("data-acc-gridrows") || root.hasAttribute("data-acc-gridrows");
        var fixedRaw = panel.getAttribute("data-acc-maxheight") || root.getAttribute("data-acc-maxheight");
        var mode = gridRows ? "gridrows" : fixedRaw ? "fixed" : "measured";
        var scrollOnOpen =
          trigger.hasAttribute("data-acc-scroll") ||
          panel.hasAttribute("data-acc-scroll") ||
          root.hasAttribute("data-acc-scroll");

        if (mode === "gridrows") {
          panel.style.display = "grid";
          panel.style.transition = "grid-template-rows " + ACC_TRANSITION_TIME + " " + ACC_TRANSITION_EASE;
          /* CSS grid collapse trick needs the direct child clipped. */
          if (panel.firstElementChild) panel.firstElementChild.style.overflow = "hidden";
        } else {
          panel.style.overflow = "hidden";
          panel.style.transition = "max-height " + ACC_TRANSITION_TIME + " " + ACC_TRANSITION_EASE;
        }

        function paint(open) {
          if (mode === "gridrows") {
            panel.style.gridTemplateRows = open ? "1fr" : "0fr";
          } else if (mode === "fixed") {
            panel.style.maxHeight = open ? parseFloat(fixedRaw) + "px" : "0px";
          } else {
            panel.style.maxHeight = open ? panel.scrollHeight + "px" : "0px";
          }
          trigger.setAttribute("aria-expanded", open ? "true" : "false");
          trigger.classList.toggle("is-open", open);
          panel.classList.toggle("is-open", open);
        }

        var isOpen = trigger.getAttribute("aria-expanded") === "true";
        paint(isOpen);

        var pair = {
          trigger: trigger,
          panel: panel,
          /* Nearest opt-in exclusivity root: the [data-zona-accordion] element
             itself if it carries the attribute, otherwise any ancestor wrapping
             several accordion instances. null = independent pair. */
          exclusiveRoot: trigger.closest ? trigger.closest("[data-acc-exclusive]") : null,
          close: function () {
            if (!isOpen) return;
            isOpen = false;
            paint(false);
          }
        };
        accPairs.push(pair);

        trigger.addEventListener("click", function () {
          isOpen = !isOpen;
          paint(isOpen);
          if (isOpen) closeSiblings(pair);
          if (isOpen && scrollOnOpen) {
            window.requestAnimationFrame(function () {
              panel.scrollIntoView({ behavior: scrollBehavior(), block: "start" });
            });
          }
        });
      });
    });
  }

  /* ----------------------------------------------------------------- reveal */

  var REVEAL_CLASS = "is-visible";

  /* pairs: [{ observed, target, threshold, rootMargin }] — grouped by
     distinct (threshold, rootMargin) pair into one IntersectionObserver
     each. Callers pass Form A and Form B pairs separately, so the two forms
     never share an observer. */
  function observeRevealGroups(pairs) {
    if (!pairs.length) return;

    if (typeof window.IntersectionObserver !== "function") {
      pairs.forEach(function (pair) {
        pair.target.classList.add(REVEAL_CLASS);
      });
      return;
    }

    var groups = {};
    pairs.forEach(function (pair) {
      var key = String(pair.threshold) + "|" + pair.rootMargin;
      (groups[key] = groups[key] || []).push(pair);
    });

    Object.keys(groups).forEach(function (key) {
      var members = groups[key];
      var targets = new Map();
      var observer = new IntersectionObserver(
        function (entries, obs) {
          entries.forEach(function (entry) {
            if (!entry.isIntersecting) return;
            /* unobserve, NEVER disconnect — this observer holds every element
               sharing this threshold and rootMargin. */
            obs.unobserve(entry.target);
            var target = targets.get(entry.target) || entry.target;
            target.classList.add(REVEAL_CLASS);
          });
        },
        { threshold: members[0].threshold, rootMargin: members[0].rootMargin }
      );
      members.forEach(function (pair) {
        targets.set(pair.observed, pair.target);
        observer.observe(pair.observed);
      });
    });
  }

  /* Form B only: find the element that should receive .is-visible. */
  function resolveRevealScope(trigger) {
    var value = trigger.getAttribute("data-reveal-trigger");
    if (value) {
      var explicit = /^[#.[]/.test(value)
        ? safeQuery(value)
        : document.getElementById(value) || safeQuery('[data-reveal-scope="' + value + '"]');
      if (explicit) return explicit;
    }
    if (trigger.hasAttribute("data-reveal-scope")) return trigger;
    var inside = trigger.querySelector("[data-reveal-scope]");
    if (inside) return inside;
    var node = trigger.parentElement;
    while (node) {
      var found = node.querySelector("[data-reveal-scope]");
      if (found) {
        /* Ancestor walk: picks the FIRST [data-reveal-scope] in document order
           inside the nearest ancestor that has one — which may belong to a
           different pair or a different section. */
        warnFallback(
          "reveal Form B fell back to an ancestor-walk scope for trigger " +
            describe(trigger) +
            " (no usable data-reveal-trigger selector, no data-reveal-scope on the trigger or in its subtree). " +
            "Resolved to " +
            describe(found) +
            ". Set an explicit data-reveal-trigger=\"#scope-id\" to make this deterministic. Trigger:",
          trigger
        );
        return found;
      }
      node = node.parentElement;
    }
    return trigger;
  }

  function initReveal() {
    var reduced = reducedMotion();

    /* Form A — self-reveal: observed element IS the revealed element. */
    var formA = [];
    $all("[data-reveal]").forEach(function (el) {
      if (reduced && el.hasAttribute("data-reveal-reduced-check")) {
        el.classList.add(REVEAL_CLASS);
        return;
      }
      formA.push({
        observed: el,
        target: el,
        threshold: numberAttr(el, "data-reveal-threshold", 0.15),
        rootMargin: el.getAttribute("data-reveal-rootmargin") || "0px"
      });
    });
    observeRevealGroups(formA);

    /* Form B — trigger -> scope: the scope may be the trigger itself. */
    var formB = [];
    $all("[data-reveal-trigger]").forEach(function (trigger) {
      var scope = resolveRevealScope(trigger);
      if (reduced && trigger.hasAttribute("data-reveal-reduced-check")) {
        scope.classList.add(REVEAL_CLASS);
        return;
      }
      formB.push({
        observed: trigger,
        target: scope,
        threshold: numberAttr(trigger, "data-reveal-trigger-threshold", 0.15),
        rootMargin: trigger.getAttribute("data-reveal-rootmargin") || "0px"
      });
    });
    observeRevealGroups(formB);
  }

  /* ------------------------------------------------------------- sticky bar */

  function initStickyBars() {
    $all("[data-zona-sticky]").forEach(function (bar) {
      var wanted = bar.getAttribute("data-zona-sticky");
      var sentinel = safeQuery(wanted);
      if (!sentinel) {
        /* Fallback: first [data-sticky-sentinel] ANYWHERE on the page — with
           more than one sticky bar or more than one sentinel this can silently
           bind to another section's boundary. */
        sentinel = document.querySelector("[data-sticky-sentinel]");
        warnFallback(
          "sticky bar " +
            describe(bar) +
            " fell back to the first [data-sticky-sentinel] on the page (data-zona-sticky value was " +
            (wanted ? '"' + wanted + '" and matched nothing' : "empty") +
            "). Resolved to " +
            describe(sentinel) +
            '. Set data-zona-sticky="#sentinel-id" to make this deterministic. Bar:',
          bar
        );
      }
      if (!sentinel) return;

      bar.style.transition = "transform 0.35s cubic-bezier(0.4, 0, 0.2, 1)";
      var shown = null;

      function render(next) {
        if (next === shown) return;
        shown = next;
        bar.style.transform = next ? "translateY(0)" : "translateY(100%)";
        bar.style.pointerEvents = next ? "auto" : "none";
        bar.classList.toggle("is-visible", next);
      }

      function onScroll() {
        render(sentinel.getBoundingClientRect().bottom <= 0);
      }

      window.addEventListener("scroll", onScroll, { passive: true });
      onScroll();
    });
  }

  /* ------------------------------------------------------ reading progress */

  function initReadingProgress() {
    $all("[data-zona-reading-progress]").forEach(function (track) {
      var fill = track.querySelector("[data-reading-progress-fill]");
      if (!fill) return;

      var ticking = false;

      function paint() {
        var root = document.documentElement;
        var distance = root.scrollHeight - root.clientHeight;
        var pct = distance > 0 ? Math.min(100, (root.scrollTop / distance) * 100) : 0;
        fill.style.width = pct + "%";
      }

      /* Deviation 5: source (81-90) sets React state on every scroll event;
         this rAF gate paints at most once per frame instead. Visual result
         identical, fewer style writes. */
      function onScroll() {
        if (ticking) return;
        ticking = true;
        requestAnimationFrame(function () {
          ticking = false;
          paint();
        });
      }

      window.addEventListener("scroll", onScroll, { passive: true });
      paint();
    });
  }

  /* ------------------------------------------------------- idle video mount */

  function mountVideo(video) {
    if (video.dataset.zonaVideoMounted === "true") return;
    video.dataset.zonaVideoMounted = "true";

    var rate = parseFloat(video.getAttribute("data-playback-rate"));
    if (!isNaN(rate)) {
      video.addEventListener(
        "loadedmetadata",
        function () {
          video.playbackRate = rate;
        },
        { once: true }
      );
    }

    var src = video.getAttribute("data-src");
    if (src) video.src = src;
    if (typeof video.load === "function") video.load();
    if (!isNaN(rate)) video.playbackRate = rate;

    if (video.autoplay && typeof video.play === "function") {
      var played = video.play();
      /* autoplay can be rejected (gesture/mute policy) — swallow it, leave the
         poster showing, never surface an unhandled rejection. */
      if (played && typeof played.catch === "function") played.catch(function () {});
    }
  }

  /* Gate 1 — TIME based (hero video, near/above the fold). */
  function initIdleVideosTimed() {
    var videos = $all("[data-zona-idlevideo]");
    if (!videos.length) return;
    var mountAll = function () {
      videos.forEach(mountVideo);
    };
    if (typeof window.requestIdleCallback === "function") {
      window.requestIdleCallback(mountAll, { timeout: 3000 });
    } else {
      window.setTimeout(mountAll, 1500);
    }
  }

  /* Gate 2 — VIEWPORT based (egg-background video, far below the fold).
     Deliberately a different mechanism, not the same gate under another name. */
  function initIdleVideosViewport() {
    var videos = $all("[data-zona-idlevideo-viewport]");
    if (!videos.length) return;

    if (typeof window.IntersectionObserver !== "function") {
      videos.forEach(mountVideo);
      return;
    }

    var observer = new IntersectionObserver(
      function (entries, obs) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          obs.unobserve(entry.target);
          mountVideo(entry.target);
        });
      },
      { rootMargin: "200px 0px" }
    );
    videos.forEach(function (video) {
      observer.observe(video);
    });
  }

  /* ------------------------------------------- two-phase scroll correction */

  function twoPhaseScrollTo(target) {
    var corrected = false;

    function correct() {
      if (corrected) return;
      corrected = true;
      window.removeEventListener("scrollend", correct);
      window.clearTimeout(timer);
      /* Deferred content above the target may still be settling layout during
         the smooth scroll — re-read, then snap flush. */
      target.getBoundingClientRect();
      target.scrollIntoView({ behavior: "instant", block: "start" });
    }

    window.addEventListener("scrollend", correct, { once: true });
    var timer = window.setTimeout(correct, 1600);
    target.scrollIntoView({ behavior: "smooth", block: "start" });
  }

  function initScrollTo() {
    document.addEventListener("click", function (event) {
      var trigger = event.target && event.target.closest && event.target.closest("[data-zona-scrollto]");
      if (!trigger) return;
      var target = safeQuery(trigger.getAttribute("data-scrollto-target") || trigger.getAttribute("data-zona-scrollto"));
      if (!target) return;
      event.preventDefault();
      twoPhaseScrollTo(target);
    });
  }

  /* ------------------------------------------------------------------ modal */

  var MODAL_TRANSITION = "0.32s cubic-bezier(0.32, 0.72, 0, 1)";
  /* The backdrop's fade is its OWN timing in source (ZonaSandbox.tsx:1775) —
     a different duration and a different curve from the sheet's transform.
     Overridable per-instance via data-modal-backdrop-transition. */
  var MODAL_BACKDROP_TRANSITION = "opacity 0.28s ease";
  var currentModal = null;
  var previousOverflow = "";

  function modalParts(root) {
    return {
      sheet: root.querySelector("[data-modal-sheet]") || root,
      backdrop: root.querySelector("[data-modal-backdrop]")
    };
  }

  function paintModal(root, open) {
    var parts = modalParts(root);
    root.style.visibility = open ? "visible" : "hidden";
    root.style.opacity = open ? "1" : "0";
    root.style.pointerEvents = open ? "auto" : "none";
    root.classList.toggle("is-open", open);
    if (parts.backdrop) parts.backdrop.style.opacity = open ? "1" : "0";
    parts.sheet.style.transform = open ? "translateY(0)" : "translateY(100%)";
  }

  function onModalKeydown(event) {
    if (event.key === "Escape" || event.key === "Esc") closeModal();
  }

  function openModal(target) {
    var root = typeof target === "string" ? safeQuery(target) : target;
    if (!root) return;
    if (currentModal && currentModal !== root) closeModal();
    if (currentModal === root) return;

    previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    currentModal = root;
    paintModal(root, true);
    document.addEventListener("keydown", onModalKeydown);
  }

  function closeModal() {
    if (!currentModal) return;
    var root = currentModal;
    currentModal = null;
    document.removeEventListener("keydown", onModalKeydown);
    document.body.style.overflow = previousOverflow;
    paintModal(root, false);
  }

  /* Full transition value (property included) for whatever element is playing
     the backdrop's fade. Override wins on the backdrop element, then the root. */
  function backdropTransition(root, backdrop) {
    return (
      (backdrop && backdrop.getAttribute("data-modal-backdrop-transition")) ||
      root.getAttribute("data-modal-backdrop-transition") ||
      MODAL_BACKDROP_TRANSITION
    );
  }

  function initModals() {
    $all("[data-zona-modal]").forEach(function (root) {
      var parts = modalParts(root);
      /* The root may be playing up to three roles at once, each with its OWN
         timing, so its transition is assembled as one shorthand list rather
         than assigned three times (later assignments would clobber earlier
         ones — notably sheet-vs-root when [data-modal-sheet] is absent).
           - backdrop fade: only when [data-modal-backdrop] is absent, in which
             case the root's own opacity IS the backdrop and must carry the
             backdrop's 0.28s ease, not the sheet's 0.32s cubic-bezier.
           - visibility: structural, always the sheet timing so the root stays
             visible until the slowest child transition has finished.
           - transform: only when [data-modal-sheet] is absent. */
      var rootTransition = [];
      if (parts.backdrop) {
        parts.backdrop.style.transition = backdropTransition(root, parts.backdrop);
        rootTransition.push("opacity " + MODAL_TRANSITION);
      } else {
        rootTransition.push(backdropTransition(root, null));
      }
      rootTransition.push("visibility " + MODAL_TRANSITION);
      if (parts.sheet === root) {
        rootTransition.push("transform " + MODAL_TRANSITION);
      } else {
        parts.sheet.style.transition = "transform " + MODAL_TRANSITION;
      }
      root.style.transition = rootTransition.join(", ");
      paintModal(root, false);

      root.addEventListener("click", function (event) {
        var backdropHit = parts.backdrop
          ? event.target === parts.backdrop || (event.target.closest && event.target.closest("[data-modal-backdrop]"))
          : event.target === root;
        if (backdropHit) closeModal();
      });
    });

    document.addEventListener("click", function (event) {
      var node = event.target;
      if (!node || !node.closest) return;

      var closer = node.closest("[data-modal-close]");
      if (closer && closer.closest("[data-zona-modal]")) {
        event.preventDefault();
        closeModal();
        return;
      }

      var trigger = node.closest("[data-zona-modal-trigger]");
      if (!trigger) return;
      var selector = trigger.getAttribute("data-modal-target") || trigger.getAttribute("data-zona-modal-trigger");
      var root = safeQuery(selector);
      if (!root) return;
      event.preventDefault();
      openModal(root);
    });
  }

  /* ------------------------------------------------------------- disclosure */

  /* ONE-WAY reveal. Distinct module from Accordion — never re-collapses. */
  function initDisclosures() {
    $all("[data-zona-disclosure]").forEach(function (root) {
      var trigger = scopedAll(root, "[data-disc-trigger]", "[data-zona-disclosure]")[0];
      var panel = scopedAll(root, "[data-disc-panel]", "[data-zona-disclosure]")[0];
      if (!trigger || !panel) return;

      trigger.addEventListener(
        "click",
        function () {
          trigger.hidden = true;
          panel.hidden = false;
          window.requestAnimationFrame(function () {
            panel.classList.add(REVEAL_CLASS);
            panel.scrollIntoView({ behavior: scrollBehavior(), block: "start" });
          });
        },
        { once: true }
      );
    });
  }

  /* ---------------------------------------------------------- nav disclosure */

  function initNavToggles() {
    $all("[data-zona-navtoggle]").forEach(function (trigger) {
      var panel =
        safeQuery(trigger.getAttribute("data-zona-navtoggle")) ||
        (trigger.getAttribute("aria-controls") ? document.getElementById(trigger.getAttribute("aria-controls")) : null);

      /* Everything past this point is a fallback: the panel was NOT pointed at
         explicitly, so it is resolved positionally and may belong to another
         section entirely. The ancestor walk reaches <body>, so it subsumes the
         document-wide query in practice; both are kept, and both warn. */
      var viaFallback = null;
      if (!panel) {
        var node = trigger.parentElement;
        while (node && !panel) {
          panel = node.querySelector("[data-nav-panel]");
          node = node.parentElement;
        }
        if (panel) viaFallback = "an ancestor walk";
      }
      if (!panel) {
        panel = document.querySelector("[data-nav-panel]");
        if (panel) viaFallback = "the first [data-nav-panel] on the page";
      }
      if (viaFallback) {
        warnFallback(
          "nav toggle " +
            describe(trigger) +
            " resolved its panel via " +
            viaFallback +
            " (neither the data-zona-navtoggle selector nor aria-controls matched). Resolved to " +
            describe(panel) +
            '. Set data-zona-navtoggle="#panel-id" or aria-controls="panel-id" to make this deterministic. Trigger:',
          trigger
        );
      }
      if (!panel) return;

      var open = trigger.getAttribute("aria-expanded") === "true";

      function paint(next) {
        open = next;
        trigger.setAttribute("aria-expanded", next ? "true" : "false");
        /* Panel is genuinely absent when closed — matches source's
           conditional render, not an always-mounted hidden element. */
        panel.hidden = !next;
        panel.classList.toggle("is-open", next);
      }

      paint(open);

      trigger.addEventListener("click", function () {
        paint(!open);
      });
    });
  }

  /* ---------------------------------------------------------------- gallery */

  function initGalleries() {
    $all("[data-zona-gallery]").forEach(function (root) {
      /* Per-instance state — never shared across gallery instances. */
      var active = 0;
      var slides = scopedAll(root, "[data-gallery-slide]", "[data-zona-gallery]");
      var thumbs = scopedAll(root, "[data-gallery-thumb]", "[data-zona-gallery]");
      var badge = scopedAll(root, "[data-gallery-badge]", "[data-zona-gallery]")[0];
      if (!slides.length) return;

      slides.forEach(function (slide) {
        slide.style.transition = "opacity 0.35s ease";
      });
      if (badge) badge.style.transition = "opacity 0.35s ease";

      function render() {
        slides.forEach(function (slide, i) {
          var on = i === active;
          slide.classList.toggle("is-active", on);
          slide.style.opacity = on ? "1" : "0";
        });
        thumbs.forEach(function (thumb, i) {
          thumb.classList.toggle("is-active", i === active);
        });
        if (badge) {
          var badgeOn = active === 0;
          badge.classList.toggle("is-active", badgeOn);
          badge.style.opacity = badgeOn ? "1" : "0";
          badge.setAttribute("aria-hidden", badgeOn ? "false" : "true");
        }
      }

      function setActive(next) {
        var clamped = Math.max(0, Math.min(next, slides.length - 1));
        if (clamped === active) return;
        active = clamped;
        render();
      }

      thumbs.forEach(function (thumb, i) {
        thumb.addEventListener("click", function () {
          setActive(i);
        });
      });

      /* Swipe surface is the slides' shared container (the viewer), so the
         thumbnail strip does not swallow or trigger swipes. */
      var viewer = slides[0].parentElement || root;
      var touchStartX = 0;
      viewer.addEventListener(
        "touchstart",
        function (event) {
          if (event.touches && event.touches.length) touchStartX = event.touches[0].clientX;
        },
        { passive: true }
      );
      viewer.addEventListener(
        "touchend",
        function (event) {
          if (!event.changedTouches || !event.changedTouches.length) return;
          var dx = event.changedTouches[0].clientX - touchStartX;
          if (Math.abs(dx) > 40) setActive(dx < 0 ? active + 1 : active - 1);
        },
        { passive: true }
      );

      render();
    });
  }

  /* --------------------------------------------------- shared pricing state */

  var PRICING = {
    subscribe: { amount: "$199", suffix: "/mo" },
    onetime: { amount: "$249", suffix: "/box" }
  };
  var pricingPlan = "subscribe";

  function isPlan(name) {
    return Object.prototype.hasOwnProperty.call(PRICING, name);
  }

  function applyAll() {
    var plan = isPlan(pricingPlan) ? pricingPlan : "subscribe";

    /* (a) every purchase card on the page, both PDPs included */
    $all("[data-purchase-card]").forEach(function (card) {
      var on = card.getAttribute("data-purchase-card") === plan;
      card.setAttribute("aria-checked", on ? "true" : "false");
      card.classList.toggle("is-selected", on);
    });

    /* (b) every hidden selling_plan input — both PDP forms AND the modal's
           own third independent cart form */
    $all('input[name="selling_plan"]').forEach(function (input) {
      if (input.dataset.sellingPlan === undefined) input.dataset.sellingPlan = input.value;
      if (plan === "subscribe" && input.dataset.sellingPlan) {
        input.value = input.dataset.sellingPlan;
        input.disabled = false;
      } else {
        input.value = "";
        input.disabled = true;
      }
    });

    /* (c) the sperm-upsell modal's price display, amount AND suffix */
    $all("[data-sperm-price]").forEach(function (el) {
      var amountEl = el.querySelector("[data-sperm-price-amount]");
      var suffixEl = el.querySelector("[data-sperm-price-suffix]");
      if (suffixEl) {
        (amountEl || el).textContent = PRICING[plan].amount;
        suffixEl.textContent = PRICING[plan].suffix;
      } else {
        el.textContent = PRICING[plan].amount + PRICING[plan].suffix;
      }
    });
  }

  function setPricingPlan(plan) {
    if (!isPlan(plan)) return;
    pricingPlan = plan;
    applyAll();
  }

  function initPricing() {
    document.addEventListener("click", function (event) {
      var card = event.target && event.target.closest && event.target.closest("[data-purchase-card]");
      if (!card) return;
      setPricingPlan(card.getAttribute("data-purchase-card"));
    });

    document.addEventListener("keydown", function (event) {
      if (event.key !== "Enter" && event.key !== " " && event.key !== "Spacebar") return;
      var card = event.target && event.target.closest && event.target.closest("[data-purchase-card]");
      if (!card) return;
      event.preventDefault();
      setPricingPlan(card.getAttribute("data-purchase-card"));
    });

    /* Runs once on init so every consumer's DOM state matches the default
       plan before any interaction (buy box vs. selected card can't disagree). */
    applyAll();
  }

  /* ------------------------------------------------------------------- boot */

  /* Shared CSS hook so later sections' baked-in per-child transition-delay
     values can be zeroed under reduced motion from ONE place, instead of every
     section re-implementing its own @media (prefers-reduced-motion) block. */
  var REDUCED_CLASS = "zona-reduced";

  function initReducedMotionHook() {
    if (reducedMotion() && document.documentElement) {
      document.documentElement.classList.add(REDUCED_CLASS);
    }
  }

  function init() {
    guard("reduced-motion-hook", initReducedMotionHook);
    guard("accordion", initAccordions);
    guard("reveal", initReveal);
    guard("sticky", initStickyBars);
    guard("reading-progress", initReadingProgress);
    guard("idlevideo", initIdleVideosTimed);
    guard("idlevideo-viewport", initIdleVideosViewport);
    guard("scrollto", initScrollTo);
    guard("modal", initModals);
    guard("disclosure", initDisclosures);
    guard("navtoggle", initNavToggles);
    guard("gallery", initGalleries);
    guard("pricing", initPricing);
  }

  ZonaV2.init = init;
  ZonaV2.openModal = openModal;
  ZonaV2.closeModal = closeModal;
  ZonaV2.scrollToTarget = twoPhaseScrollTo;
  ZonaV2.pricing = {
    get plan() {
      return pricingPlan;
    },
    set: setPricingPlan,
    applyAll: applyAll
  };

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
})();
